// ChartCard — Phase 5 of the Site Overview redesign (djin-cloud#67).
//
// Row 2 left slot (col-span-8). Replaces the Phase-5 placeholder with a
// curated-preset historian chart. The chart library is CHART_PRESETS (see
// `web/src/config/chartPresets.ts`); only presets whose `availabilityRule`
// passes at the current site are offered in the dropdown.
//
// Persistence:
//   - Selection lives in `localStorage` under
//     `site-overview-chart-preset:{site_id}` (per-site scope).
//   - Interval is session-only — every mount starts at the preset's
//     `intervalDefault`; selecting a new preset RESETS interval to the new
//     preset's default.
//
// Design contract: docs/plans/2026-04-20-site-overview-redesign-design.md §4.5.
// Mockup: docs/plans/2026-04-20-site-overview-mockup.html (row 2 left).
//
// Workspace rules honored:
//   - [No fabricated fallback data]: empty historian response → empty chart
//     via `InlineTimeSeriesChart`'s existing "No data available" branch. No
//     synthesized series.
//   - Pane-of-glass read-only: no write controls on this card.
//   - SSR-safe: all `window.localStorage` access is gated inside `useEffect`
//     or event handlers, never during render.

import { useCallback, useEffect, useRef, useState } from 'react';
import { InlineTimeSeriesChart } from '../../charts/InlineTimeSeriesChart';
import {
  CHART_PRESETS,
  DEFAULT_CHART_PRESET_ID,
  getAvailablePresets,
  resolvePresetId,
} from '../../../config/chartPresets';
import type {
  ChartIntervalShorthand,
  ChartPreset,
  ChartPresetId,
} from '../../../types/chart.types';
import type { SiteConfig } from '../../../types/project.types';

export interface ChartCardProps {
  siteId: string;
  site: SiteConfig;
}

function storageKeyFor(siteId: string) {
  return `site-overview-chart-preset:${siteId}`;
}

function readStoredPresetId(siteId: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(storageKeyFor(siteId));
  } catch {
    return null;
  }
}

function writeStoredPresetId(siteId: string, id: ChartPresetId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKeyFor(siteId), id);
  } catch {
    // Quota / disabled storage: silently ignore. Selection is low-stakes.
  }
}

/**
 * Multi-series default palette for `power_frequency`. Order matches the
 * comma-separated `metric` split: first entry = total_load_kw, second = grid_frequency_hz.
 */
const POWER_FREQ_COLORS = ['#00D4FF', '#F59E0B'];

function buildMultiSeries(preset: ChartPreset): Array<{
  kpiName: string;
  name: string;
  color: string;
  unit: string;
}> | undefined {
  if (!preset.metric.includes(',')) return undefined;
  const kpis = preset.metric.split(',').map((s) => s.trim());
  return kpis.map((kpiName, i) => ({
    kpiName,
    name: humanizeMetric(kpiName),
    color: POWER_FREQ_COLORS[i % POWER_FREQ_COLORS.length],
    // DB-canonical metric names (no unit suffix). See chartPresets.ts.
    unit: kpiName === 'grid_frequency' ? 'Hz' : 'kW',
  }));
}

function humanizeMetric(m: string): string {
  if (m === 'total_load') return 'Power';
  if (m === 'grid_frequency') return 'Frequency';
  return m;
}

export function ChartCard({ siteId, site }: ChartCardProps) {
  const available = getAvailablePresets(site);
  // Seed state synchronously with the default so the first render is SSR-safe
  // (no localStorage read during render). An effect hydrates from storage once
  // the component mounts on the client.
  const [presetId, setPresetId] = useState<ChartPresetId>(DEFAULT_CHART_PRESET_ID);
  const [interval, setInterval] = useState<ChartIntervalShorthand>(
    CHART_PRESETS[DEFAULT_CHART_PRESET_ID].intervalDefault,
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // Hydrate from localStorage once per siteId. Kept in an effect so SSR
  // (render path) never touches `window`.
  useEffect(() => {
    const stored = readStoredPresetId(siteId);
    const resolved = resolvePresetId(stored, site);
    setPresetId(resolved);
    setInterval(CHART_PRESETS[resolved].intervalDefault);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- site passed by caller; rehydrate only on siteId change
  }, [siteId]);

  const handleSelectPreset = useCallback(
    (id: ChartPresetId) => {
      setPresetId(id);
      setInterval(CHART_PRESETS[id].intervalDefault);
      writeStoredPresetId(siteId, id);
      setMenuOpen(false);
      // Return focus to the button for keyboard users.
      buttonRef.current?.focus();
    },
    [siteId],
  );

  const handleSelectInterval = useCallback((v: ChartIntervalShorthand) => {
    // Interval is session-only — do NOT write localStorage.
    setInterval(v);
  }, []);

  // Click-outside-closes
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (menuRef.current.contains(e.target as Node)) return;
      if (buttonRef.current?.contains(e.target as Node)) return;
      setMenuOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  // Esc closes
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        buttonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const preset = CHART_PRESETS[presetId];
  const multiSeries = buildMultiSeries(preset);
  // Degrade `dual-axis` preset to multi-series line (single shared Y-axis)
  // until AreaChart/LineChart gain a second-axis prop. See TODO in chartPresets.ts.
  const effectiveChartType: 'bar' | 'line' | 'area' =
    preset.chartType === 'dual-axis' ? 'line' : preset.chartType;

  return (
    <section
      data-testid="chart-card"
      className="card rounded-xl border border-ems-border-bright/50 overflow-hidden bg-ems-bg-card/60"
    >
      {/* Header: preset dropdown · range segmented · live dot */}
      <div className="px-5 py-3 flex items-center gap-3 border-b border-ems-border">
        {/* Preset dropdown */}
        <div className="relative">
          <button
            ref={buttonRef}
            type="button"
            data-testid="chart-card-preset-button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-md border border-ems-border bg-ems-bg-card/40 px-2.5 py-1 text-xs font-medium text-ems-text-primary hover:bg-ems-bg-card/70 focus:outline-none focus:ring-1 focus:ring-ems-border-bright"
          >
            <span>{preset.label}</span>
            <span aria-hidden="true" className="text-ems-text-muted">▾</span>
          </button>
          {menuOpen && (
            <div
              ref={menuRef}
              role="menu"
              data-testid="chart-card-preset-menu"
              className="absolute left-0 top-full z-10 mt-1 min-w-[14rem] rounded-md border border-ems-border bg-ems-bg-card shadow-lg"
            >
              {available.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="menuitem"
                  data-testid={`chart-card-preset-option-${p.id}`}
                  aria-current={p.id === presetId}
                  onClick={() => handleSelectPreset(p.id)}
                  className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-ems-bg-card/60 ${
                    p.id === presetId
                      ? 'text-ems-cyan font-semibold'
                      : 'text-ems-text-primary'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Range segmented control */}
        <div
          className="flex items-center gap-1"
          role="group"
          aria-label="Time range"
          data-testid="chart-card-interval-group"
        >
          {preset.intervalOptions.map((v) => (
            <button
              key={v}
              type="button"
              data-testid={`chart-card-interval-${v}`}
              aria-pressed={v === interval}
              onClick={() => handleSelectInterval(v)}
              className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${
                v === interval
                  ? 'bg-ems-cyan text-ems-navy'
                  : 'bg-ems-bg-card/40 text-ems-text-muted hover:text-ems-text-primary'
              }`}
            >
              {v.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Live pulse dot */}
        <div className="ml-auto flex items-center gap-1.5">
          <span
            className="w-1.5 h-1.5 rounded-full bg-ems-green animate-ems-pulse"
            aria-hidden="true"
            data-testid="chart-card-live-dot"
          />
          <span className="text-[10px] text-ems-text-muted uppercase tracking-wider">Live</span>
        </div>
      </div>

      {/* Body */}
      <div className="p-3">
        <InlineTimeSeriesChart
          // Key forces re-mount on preset/interval change so react-query
          // refetches cleanly with the new params without relying on stale
          // cached data from a differently-scoped query.
          key={`${preset.id}:${interval}`}
          siteId={siteId}
          assetType={preset.assetType}
          kpiName={multiSeries ? multiSeries[0].kpiName : preset.metric}
          title={preset.label}
          color="#00D4FF"
          unit={preset.unit}
          chartType={effectiveChartType}
          height={240}
          yAxisDomain={preset.yDomain ?? ['auto', 'auto']}
          showRangeSelector={false}
          intervalOverride={interval}
          multiSeries={multiSeries}
          showLegend={!!multiSeries}
        />
      </div>
    </section>
  );
}

export default ChartCard;
